// ClientLots.js
import React, { useState } from 'react';
import axiosInstance from '../../axiosConfig';
import GlassPaper from '../GlassPaper';
import { useAdminStrategies } from '../../hooks/useAdminStrategies';
import { strategyModelLabel } from '../../utils/strategyAccountModel';
import './ClientLots.css';

const ClientLots = () => {
    const { legacyStrategies, loading: strategiesLoading } = useAdminStrategies();
    const [selectedStrategy, setSelectedStrategy] = useState('');
    const [strategyDesc, setStrategyDesc] = useState('');
    const [clientLots, setClientLots] = useState([]);
    const [editedValues, setEditedValues] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [responseColor, setResponseColor] = useState('');
    const [hasChanges, setHasChanges] = useState(false);

    const handleStrategyChange = async (e) => {
        const selected = e.target.value;
        setSelectedStrategy(selected);
        const strategy = legacyStrategies.find((strategy) => strategy.strategy_name === selected);
        setStrategyDesc(strategy ? strategy.strategy_desc : '');
        setResponseMessage('');
        setHasChanges(false);
        setEditedValues({});

        if (selected) {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/api/client-lots/${selected}`);
                setClientLots(response.data);

                // Initialize edited values with current values
                const initialValues = {};
                response.data.forEach(client => {
                    initialValues[client.app_name] = {
                        lots: client.lots,
                        qty: client.qty,
                        original_lots: client.lots,
                        original_qty: client.qty
                    };
                });
                setEditedValues(initialValues);
            } catch (error) {
                console.error('Error fetching client lots:', error);
                setResponseMessage('Error loading client lots data');
                setResponseColor('red');
                setClientLots([]);
            } finally {
                setLoading(false);
            }
        } else {
            setClientLots([]);
            setEditedValues({});
        }
    };

    const handleValueChange = (appName, field, value) => {
        const updatedValues = {
            ...editedValues,
            [appName]: {
                ...editedValues[appName],
                [field]: value
            }
        };
        setEditedValues(updatedValues);

        // Check if there are any changes
        const changed = Object.keys(updatedValues).some(key => {
            const current = updatedValues[key];
            return current.lots !== current.original_lots ||
                   current.qty !== current.original_qty;
        });
        setHasChanges(changed);
    };

    const handleSave = async () => {
        if (!hasChanges) {
            setResponseMessage('No changes to save');
            setResponseColor('orange');
            return;
        }

        setSaving(true);
        try {
            // Collect changed records
            const updates = [];
            Object.keys(editedValues).forEach(appName => {
                const current = editedValues[appName];
                if (current.lots !== current.original_lots || current.qty !== current.original_qty) {
                    updates.push({
                        app_name: appName,
                        strategy_name: selectedStrategy,
                        lots: parseInt(current.lots) || 0,
                        qty: parseInt(current.qty) || 0
                    });
                }
            });

            await axiosInstance.post('/api/client-lots', {
                updates: updates
            });

            setResponseMessage(`Successfully updated ${updates.length} client allocation(s)`);
            setResponseColor('green');

            // Update original values to reflect saved state
            const newEditedValues = { ...editedValues };
            updates.forEach(update => {
                newEditedValues[update.app_name].original_lots = update.lots;
                newEditedValues[update.app_name].original_qty = update.qty;
            });
            setEditedValues(newEditedValues);
            setHasChanges(false);

            // Refresh data from server
            const response = await axiosInstance.get(`/api/client-lots/${selectedStrategy}`);
            setClientLots(response.data);
        } catch (error) {
            console.error('Error saving client lots:', error);
            setResponseMessage('Error saving client lots');
            setResponseColor('red');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        // Reset to original values
        const resetValues = {};
        Object.keys(editedValues).forEach(appName => {
            const current = editedValues[appName];
            resetValues[appName] = {
                lots: current.original_lots,
                qty: current.original_qty,
                original_lots: current.original_lots,
                original_qty: current.original_qty
            };
        });
        setEditedValues(resetValues);
        setHasChanges(false);
        setResponseMessage('');
    };

    return (
        <GlassPaper className="client-lots-shell" elevation={0} square={false}>
            <h2 className="dashboard-title">Client Lots</h2>
            <p className="description-text">
                Legacy algoverse strategies only (<code>ca_client_lots</code>). quant_platform
                strategies use the <strong>Strategy accounts</strong> tab instead.
            </p>

            <div className="form-group mt-3">
                <label htmlFor="strategySelectLots">Strategy Name</label>
                <select
                    id="strategySelectLots"
                    className="form-control"
                    value={selectedStrategy}
                    onChange={handleStrategyChange}
                    disabled={strategiesLoading}
                >
                    <option value="">Select a legacy strategy</option>
                    {legacyStrategies.map((strategy, index) => (
                        <option key={index} value={strategy.strategy_name}>
                            {strategy.strategy_name}
                        </option>
                    ))}
                </select>
                {strategyDesc && <p className="description-text">{strategyDesc}</p>}
                {selectedStrategy && (
                    <p className="description-text">
                        {strategyModelLabel(legacyStrategies.find((s) => s.strategy_name === selectedStrategy))}
                    </p>
                )}
            </div>

            {loading && (
                <div className="loading-message">Loading client lots...</div>
            )}

            {!loading && selectedStrategy && clientLots.length > 0 && (
                <div className="client-lots-table-container">
                    <table className="client-lots-table">
                        <thead>
                            <tr>
                                <th>Client Name</th>
                                <th>Lots</th>
                                <th>Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clientLots.map((client, index) => (
                                <tr key={index}>
                                    <td className="client-name-cell">
                                        <div className="client-name">{client.app_name}</div>
                                        {client.account_name && (
                                            <div className="client-alias">{client.account_name}</div>
                                        )}
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={editedValues[client.app_name]?.lots || 0}
                                            min="0"
                                            step="1"
                                            onChange={(e) => handleValueChange(
                                                client.app_name,
                                                'lots',
                                                parseInt(e.target.value) || 0
                                            )}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={editedValues[client.app_name]?.qty || 0}
                                            min="0"
                                            step="1"
                                            onChange={(e) => handleValueChange(
                                                client.app_name,
                                                'qty',
                                                parseInt(e.target.value) || 0
                                            )}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && selectedStrategy && clientLots.length === 0 && (
                <div className="no-data-message">
                    No client lot allocations found for this strategy.
                </div>
            )}

            {!legacyStrategies.length && !strategiesLoading && (
                <div className="no-data-message">
                    No legacy strategies in catalog. quant_platform strategies appear under Strategy accounts.
                </div>
            )}

            {selectedStrategy && clientLots.length > 0 && (
                <div className="action-buttons">
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleReset}
                        disabled={!hasChanges || saving}
                    >
                        Reset
                    </button>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            )}

            {responseMessage && (
                <div className="response-message" style={{ color: responseColor }}>
                    {responseMessage}
                    <button
                        className="btn btn-secondary btn-sm mt-2"
                        onClick={() => setResponseMessage('')}
                    >
                        Ok
                    </button>
                </div>
            )}
        </GlassPaper>
    );
};

export default ClientLots;
